// ============================================================================
// StateService — app-wide UI state: theme, brand palette, sidebar, language.
// Persisted to localStorage under one key; theme + palette + lang are reflected
// onto <html data-theme|data-palette|lang> so the pre-paint script in
// index.html stays in sync (no flash of the wrong palette on reload).
// ============================================================================
import { createEffect, createRoot } from 'solid-js';
import { useLang, type Lang } from '~/lib/i18n';
import { signal } from '~/lib/signal';

export type Theme = 'dark' | 'light';
/** ONE brand, four product-wide accent directions (coral default). */
export type Palette = 'svrnty' | 'coral' | 'jade' | 'violet';
export type { Lang };
/** Tickets-page layout (the inbox list/lanes toggle). */
export type InboxLayout = 'list' | 'lanes';

const STORAGE_KEY = 'lumendesk.console.v1';
const PALETTES: readonly Palette[] = ['svrnty', 'coral', 'jade', 'violet'];
const LANGS: readonly Lang[] = ['en-CA', 'fr-CA'];
const INBOX_LAYOUTS: readonly InboxLayout[] = ['list', 'lanes'];

interface PersistedState {
  theme: Theme;
  palette: Palette;
  sidebarCollapsed: boolean;
  lang: Lang;
  inboxLayout: InboxLayout;
}

export class StateService {
  private readonly persisted = this.read();

  readonly theme = signal<Theme>(this.persisted.theme);
  readonly palette = signal<Palette>(this.persisted.palette);
  readonly sidebarCollapsed = signal<boolean>(this.persisted.sidebarCollapsed);
  readonly lang = signal<Lang>(this.persisted.lang);
  /** Tickets-page list/lanes preference — remembered per browser. */
  readonly inboxLayout = signal<InboxLayout>(this.persisted.inboxLayout);
  /** Mobile off-canvas nav (≤860px); not persisted. */
  readonly mobileNavOpen = signal<boolean>(false);

  constructor() {
    // Reflect theme + palette + lang onto <html>, load the language, and
    // persist on any change. Detached root: lives for the whole app.
    createRoot(() =>
      createEffect(
        () => ({
          theme: this.theme(),
          palette: this.palette(),
          sidebarCollapsed: this.sidebarCollapsed(),
          lang: this.lang(),
          inboxLayout: this.inboxLayout(),
        }),
        (s) => {
          if (typeof document !== 'undefined') {
            const el = document.documentElement;
            el.setAttribute('data-theme', s.theme);
            el.setAttribute('data-palette', s.palette);
            el.setAttribute('lang', s.lang);
          }
          void useLang(s.lang);
          this.write(s);
        },
      ),
    );
  }

  setInboxLayout(layout: InboxLayout): void {
    this.inboxLayout.set(layout);
  }

  toggleTheme(): void {
    this.theme.update((t) => (t === 'dark' ? 'light' : 'dark'));
  }

  setTheme(theme: Theme): void {
    this.theme.set(theme);
  }

  setPalette(palette: Palette): void {
    this.palette.set(palette);
  }

  setLang(lang: Lang): void {
    this.lang.set(lang);
  }

  toggleLang(): void {
    this.lang.update((l) => (l === 'en-CA' ? 'fr-CA' : 'en-CA'));
  }

  toggleSidebar(): void {
    this.sidebarCollapsed.update((c) => !c);
  }

  toggleMobileNav(): void {
    this.mobileNavOpen.update((o) => !o);
  }

  closeMobileNav(): void {
    this.mobileNavOpen.set(false);
  }

  private read(): PersistedState {
    const fallback: PersistedState = {
      theme: 'dark',
      palette: 'coral',
      sidebarCollapsed: false,
      lang: 'en-CA',
      inboxLayout: 'list',
    };
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return fallback;
      const parsed = JSON.parse(raw) as Partial<PersistedState>;
      return {
        theme: parsed.theme === 'light' ? 'light' : 'dark',
        palette: PALETTES.includes(parsed.palette as Palette) ? (parsed.palette as Palette) : 'coral',
        sidebarCollapsed: !!parsed.sidebarCollapsed,
        lang: LANGS.includes(parsed.lang as Lang) ? (parsed.lang as Lang) : 'en-CA',
        inboxLayout: INBOX_LAYOUTS.includes(parsed.inboxLayout as InboxLayout)
          ? (parsed.inboxLayout as InboxLayout)
          : 'list',
      };
    } catch {
      return fallback;
    }
  }

  private write(state: PersistedState): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch {
      /* storage unavailable — ignore */
    }
  }
}

export const stateService = new StateService();
